import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { restoreScrollPosition, saveScrollPosition } from '../utils/scrollRestoration';

/**
 * Preserves vertical scroll across full page refreshes and scrolls to top on
 * in-app route changes. Prevents the browser from clamping restored scroll
 * to the bottom while auth/loading spinners are still short.
 */
export default function ScrollRestoration() {
  const location = useLocation();
  const hasHandledInitialLoad = useRef(false);
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    const pathname = location.pathname;

    const persistScroll = () => saveScrollPosition(pathname);
    window.addEventListener('pagehide', persistScroll);

    let scrollTimeout;
    const onScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(persistScroll, 150);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('pagehide', persistScroll);
      window.removeEventListener('scroll', onScroll);
      clearTimeout(scrollTimeout);
      persistScroll();
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!hasHandledInitialLoad.current) {
      hasHandledInitialLoad.current = true;
      const cancelRestore = restoreScrollPosition(location.pathname);
      previousPathname.current = location.pathname;
      return cancelRestore;
    }

    if (previousPathname.current !== location.pathname) {
      window.scrollTo(0, 0);
      saveScrollPosition(location.pathname, 0);
      previousPathname.current = location.pathname;
    }

    return undefined;
  }, [location.pathname]);

  return null;
}
